import logger from "../../utils/logger";
import { supabase, getUserProfile, ensureValidSession } from './client';
import { canCreateProperty } from './subscriptions';

/**
 * Get all properties for the current user (owned properties only)
 * Note: Tenant-invited properties are fetched separately via getTenantProperties()
 * @returns {Promise<Array>} Array of owned properties
 */
export async function getProperties() {
  try {
    logger.log('[getProperties] Starting fetch...');
    const startTime = Date.now();

    // Ensure session is valid before making request
    const isValid = await ensureValidSession();
    if (!isValid) {
      console.warn('[getProperties] Invalid session, returning empty array');
      return [];
    }

    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      logger.log('[getProperties] No user, returning empty array');
      return [];
    }

    // Fetch only owned properties (explicit user_id filter avoids RLS recursion)
    const { data, error } = await supabase
      .from('properties')
      .select('*')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false });

    const duration = Date.now() - startTime;
    logger.log(`[getProperties] Fetch completed in ${duration}ms`, {
      count: data?.length || 0,
      hasError: !!error,
    });

    if (error) {
      console.error('[getProperties] Supabase error:', error);
      throw error;
    }
    return data || [];
  } catch (error) {
    console.error('[getProperties] Error:', error);
    throw error;
  }
}

/**
 * Get a single property by ID
 * @param {string} id - Property ID
 * @returns {Promise<Object>} Property object
 */
export async function getProperty(id) {
  try {
    const { data, error } = await supabase
      .from('properties')
      .select('*')
      .eq('id', id)
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error fetching property:', error);
    throw error;
  }
}

/**
 * Get all countries
 * @returns {Promise<Array>} Array of countries
 */
export async function getCountries() {
  try {
    const { data, error } = await supabase
      .from('countries')
      .select('*')
      .eq('is_active', true)
      .order('name');

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching countries:', error);
    throw error;
  }
}

/**
 * Create a new property
 * @param {Object} property - Property data
 * @returns {Promise<Object>} Created property
 */
export async function createProperty(property) {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    // Check subscription limits
    const profile = await getUserProfile(user.id);
    const permission = canCreateProperty(profile);

    if (!permission.allowed) {
      const error = new Error(permission.reason);
      error.requiresUpgrade = permission.requiresUpgrade;
      throw error;
    }

    const propertyData = {
      user_id: user.id,
      // Property type
      type: property.type,

      // Address fields (required except apartment_number)
      street: property.street,
      building_number: property.buildingNumber,
      apartment_number: property.apartmentNumber || null,
      city: property.city,
      postal_code: property.postalCode,
      country_code: property.countryCode || 'PL',

      // Property attributes
      has_garage: property.hasGarage || false,
      has_storage_room: property.hasStorageRoom || false,
      total_area: property.totalArea || null,
      floor_number: property.floorNumber || null,

      // Rental status (general availability)
      rental_status: property.rentalStatus || 'available',

      // File paths
      primary_photo_path: property.primaryPhotoPath || null,
      contract_attachment_path: property.contractAttachmentPath || null,
    };

    const { data, error } = await supabase
      .from('properties')
      .insert([propertyData])
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error creating property:', error);
    throw error;
  }
}

/**
 * Update an existing property
 * @param {string} id - Property ID
 * @param {Object} updates - Property updates
 * @returns {Promise<Object>} Updated property
 */
export async function updateProperty(id, updates) {
  try {
    const updateData = {
      // Property type
      type: updates.type,

      // Address fields
      street: updates.street,
      building_number: updates.buildingNumber,
      apartment_number: updates.apartmentNumber || null,
      city: updates.city,
      postal_code: updates.postalCode,
      country_code: updates.countryCode || 'PL',

      // Property attributes
      has_garage: updates.hasGarage || false,
      has_storage_room: updates.hasStorageRoom || false,
      total_area: updates.totalArea || null,
      floor_number: updates.floorNumber || null,

      // Rental status (general availability)
      rental_status: updates.rentalStatus || 'available',

      // File paths
      primary_photo_path: updates.primaryPhotoPath || null,
      contract_attachment_path: updates.contractAttachmentPath || null,
    };

    const { data, error } = await supabase
      .from('properties')
      .update(updateData)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error updating property:', error);
    throw error;
  }
}

/**
 * Delete a property
 * @param {string} id - Property ID
 * @returns {Promise<void>}
 */
export async function deleteProperty(id) {
  try {
    const { error } = await supabase
      .from('properties')
      .delete()
      .eq('id', id);

    if (error) throw error;
  } catch (error) {
    console.error('Error deleting property:', error);
    throw error;
  }
}

/**
 * Get property count for current user
 * @returns {Promise<number>} Property count
 */
export async function getPropertyCount() {
  try {
    const { count, error } = await supabase
      .from('properties')
      .select('*', { count: 'exact', head: true });

    if (error) throw error;
    return count || 0;
  } catch (error) {
    console.error('Error counting properties:', error);
    throw error;
  }
}

/**
 * Update property rental status
 * @param {string} id - Property ID
 * @param {string} status - Rental status (available, rented, maintenance, archived)
 * @returns {Promise<Object>} Updated property
 */
export async function updatePropertyRentalStatus(id, status) {
  try {
    const { data, error } = await supabase
      .from('properties')
      .update({ rental_status: status })
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error updating property rental status:', error);
    throw error;
  }
}
